
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Menu, Bell } from 'lucide-react';
import Sidebar from '../Sidebar/Sidebar';
import Footer from '../Footer/footer';
import NotificationDropdown from '../Notifications/NotificationDropdown';
import ConfirmPopup from '../Popups/ConfirmPopup';
import MessagePopup from '../Popups/MessagePopup';
import { useNotifications } from '../../hooks/useNotifications';
import './AppLayout.css';

const CLEARED_KEY = 'rismic_cleared_notifications';

const AppLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const notificationRef = useRef(null);
  const refreshIntervalRef = useRef(null);

  // ==================== USE NOTIFICATIONS HOOK ====================
  const {
    notificationCount: rawNotificationCount,
    showNotificationDropdown,
    pendingNotifications,
    cancelledNotifications,
    socketConnected,
    handleAcceptOrder,
    handleRejectOrder,
    toggleNotificationDropdown,
    closeNotificationDropdown,
    navigateToHistory,
    fetchActiveOrders,
  } = useNotifications();

  // ==================== CLEARED NOTIFICATIONS ====================
  const [clearedNotificationIds, setClearedNotificationIds] = useState(() => {
    try {
      const saved = localStorage.getItem(CLEARED_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(CLEARED_KEY, JSON.stringify(clearedNotificationIds));
    } catch {
      // ignore storage error
    }
  }, [clearedNotificationIds]);

  const isOrderCleared = useCallback(
    (orderId) => clearedNotificationIds.includes(orderId),
    [clearedNotificationIds]
  );

  const handleClearNotification = useCallback((orderId) => {
    setClearedNotificationIds((prev) =>
      prev.includes(orderId) ? prev : [...prev, orderId]
    );
  }, []);

  // If same order is re-assigned / becomes confirmed again, treat as new
  useEffect(() => {
    if (!pendingNotifications || pendingNotifications.length === 0) return;

    setClearedNotificationIds((prev) => {
      const pendingById = new Map(
        pendingNotifications.map((o) => [o._id, o])
      );
      const next = prev.filter((id) => {
        const order = pendingById.get(id);
        // Rule: if order is back to 'confirmed', remove from cleared
        if (order && order.status === 'confirmed') return false;
        return true;
      });
      return next;
    });
  }, [pendingNotifications]);

  // Filter out cleared notifications
  const activePendingNotifications = pendingNotifications.filter(
    (order) => order._id && !isOrderCleared(order._id)
  );
  const activeCancelledNotifications = cancelledNotifications.filter(
    (order) => order._id && !isOrderCleared(order._id)
  );

  // Count based on active (not cleared)
  const notificationCount =
    activePendingNotifications.length + activeCancelledNotifications.length;

  // ==================== POPUP STATES ====================
  const [confirmPopup, setConfirmPopup] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: null,
  });

  const [messagePopup, setMessagePopup] = useState({
    show: false,
    title: '',
    message: '',
    type: 'info',
  });

  // ==================== AUTO REFRESH EVERY 2 SECONDS ====================
  useEffect(() => {
    console.log('🔄 Starting auto-refresh interval (every 2 seconds)...');

    fetchActiveOrders();

    refreshIntervalRef.current = setInterval(() => {
      console.log('🔄 Auto-refreshing orders... (2s interval)');
      fetchActiveOrders();
    }, 2000);

    return () => {
      if (refreshIntervalRef.current) {
        console.log('🛑 Stopping auto-refresh interval');
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [fetchActiveOrders]);

  // ==================== CLOSE DROPDOWN WHEN CLICKING OUTSIDE ====================
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        closeNotificationDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [closeNotificationDropdown]);

  // ==================== POPUP HELPERS ====================
  const showConfirmPopup = useCallback((title, message, onConfirm) => {
    setConfirmPopup({ show: true, title, message, onConfirm });
  }, []);

  const closeConfirmPopup = useCallback(() => {
    setConfirmPopup({ show: false, title: '', message: '', onConfirm: null });
  }, []);

  const showMessagePopup = useCallback((title, message, type = 'info') => {
    setMessagePopup({ show: true, title, message, type });
  }, []);

  const closeMessagePopup = useCallback(() => {
    setMessagePopup({ show: false, title: '', message: '', type: 'info' });
  }, []);

  // ==================== HANDLE ACCEPT ORDER ====================
  const onAcceptOrder = useCallback(
    async (orderId) => {
      console.log('📦 Accept order clicked:', orderId);

      try {
        await handleAcceptOrder(orderId);
        showMessagePopup(
          'Order Accepted',
          'Order has been accepted successfully! You can now start the delivery.',
          'success'
        );

        setTimeout(() => {
          fetchActiveOrders();
        }, 500);
      } catch (error) {
        console.error('❌ Error accepting order:', error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          'Failed to accept order. Please try again.';
        showMessagePopup('Error', errorMessage, 'error');
      }
    },
    [handleAcceptOrder, showMessagePopup, fetchActiveOrders]
  );

  // ==================== HANDLE REJECT ORDER ====================
  const onRejectOrder = useCallback(
    (orderId) => {
      console.log('🚫 Reject order clicked:', orderId);

      showConfirmPopup(
        'Reject Order?',
        'Are you sure you want to reject this order?\n\nThe order will be returned to pending status and will be available for other delivery agents to accept.',
        async () => {
          try {
            await handleRejectOrder(orderId);
            showMessagePopup(
              'Order Rejected',
              'Order has been rejected successfully. It will now be available for other delivery agents.',
              'success'
            );

            setTimeout(() => {
              fetchActiveOrders();
            }, 500);
          } catch (error) {
            console.error('❌ Error rejecting order:', error);
            const errorMessage =
              error.response?.data?.message ||
              error.message ||
              'Failed to reject order. Please try again.';
            showMessagePopup('Error', errorMessage, 'error');
          }
        }
      );
    },
    [handleRejectOrder, showConfirmPopup, showMessagePopup, fetchActiveOrders]
  );

  // ==================== HANDLE VIEW ORDER DETAILS ====================
  const onViewOrderDetails = useCallback((order) => {
    console.log('👁️ View order details:', order);
    // You can implement navigation or modal here
  }, []);

  // ==================== MANUAL REFRESH ====================
  const handleManualRefresh = useCallback(() => {
    console.log('🔄 Manual refresh triggered');
    fetchActiveOrders();
  }, [fetchActiveOrders]);

  // ==================== RENDER ====================
  return (
    <div className="app-layout">
      {/* ==================== SIDEBAR ==================== */}
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* ==================== TOPBAR ==================== */}
      <header className="topbar">
        <button
          className="menu-btn"
          onClick={() => setSidebarOpen(true)}
          aria-label="Open menu"
        >
          <Menu size={24} />
        </button>
        <img
          src="https://res.cloudinary.com/djocp8373/image/upload/v1763446431/weblogo_rbpcix.png"
          alt="Rismic Grocery Logo"
          className="topbar-logo"
        />

        {/* ==================== NOTIFICATION BELL ==================== */}
        <div className="notification-container" ref={notificationRef}>
          <button
            className={`notification-btn ${
              notificationCount > 0 ? 'has-notifications' : ''
            }`}
            title="Notifications"
            onClick={toggleNotificationDropdown}
            aria-label={`Notifications ${
              notificationCount > 0 ? `(${notificationCount} new)` : ''
            }`}
          >
            <Bell size={24} />
            {notificationCount > 0 && (
              <span
                className="notification-badge"
                aria-label={`${notificationCount} unread notifications`}
              >
                {notificationCount > 99 ? '99+' : notificationCount}
              </span>
            )}
          </button>

          {/* ==================== NOTIFICATION DROPDOWN ==================== */}
          <NotificationDropdown
            show={showNotificationDropdown}
            notificationCount={notificationCount}
            pendingNotifications={activePendingNotifications}
            cancelledNotifications={activeCancelledNotifications}
            onAccept={onAcceptOrder}
            onReject={onRejectOrder}
            onViewDetails={onViewOrderDetails}
            onViewAll={navigateToHistory}
            onClose={closeNotificationDropdown}
            onClearNotification={handleClearNotification} // <= NEW
          />
        </div>
      </header>

      {/* ==================== MAIN CONTENT ==================== */}
      <main className="app-main-content">{children}</main>

      {/* ==================== FOOTER ==================== */}
      <Footer />

      {/* ==================== CONFIRM POPUP ==================== */}
      <ConfirmPopup
        show={confirmPopup.show}
        title={confirmPopup.title}
        message={confirmPopup.message}
        type="danger"
        confirmText="Yes, Reject"
        cancelText="Cancel"
        onConfirm={() => {
          if (confirmPopup.onConfirm) {
            confirmPopup.onConfirm();
          }
          closeConfirmPopup();
        }}
        onCancel={closeConfirmPopup}
      />

      {/* ==================== MESSAGE POPUP ==================== */}
      <MessagePopup
        show={messagePopup.show}
        title={messagePopup.title}
        message={messagePopup.message}
        type={messagePopup.type}
        buttonText="OK"
        onClose={closeMessagePopup}
      />
    </div>
  );
};

export default AppLayout;

